import { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ApiSwatidEventsCompareResponse } from '@/types/api';
import type { ApiSwatidRelay } from '@/types/api';
import {
  buildTimelineRows,
  formatHourLabel,
  type SwatidTimelineRow,
} from '@/utils/swatidEvents';
import { getSwatidRelayOptionLabel } from '@/utils/swatidRelay';
import { cn } from '@/lib/utils';

interface SwatidScheduleTimelineProps {
  compareData: ApiSwatidEventsCompareResponse;
  relays: ApiSwatidRelay[];
}

const HOUR_TICKS = [0, 6, 12, 18, 24];

const statusStyles: Record<SwatidTimelineRow['status'], string> = {
  ok: 'border-success/40',
  warning: 'border-amber-500/40',
  error: 'border-destructive/40',
  idle: 'border-border',
};

function TimelineRowView({ row }: { row: SwatidTimelineRow }) {
  return (
    <div className={cn('rounded-lg border p-3 space-y-2', statusStyles[row.status])}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium truncate">{row.relayLabel}</span>
          <Badge variant="outline" className="text-[10px] shrink-0">
            R{row.relayNumber}
          </Badge>
        </div>
        <div className="flex gap-1 flex-wrap">
          {row.schedules.length > 0 && (
            <Badge variant="secondary" className="text-[10px]">
              {row.schedules.length} prog.
            </Badge>
          )}
          {row.events.length > 0 && (
            <Badge variant="outline" className="text-[10px]">
              {row.events.length} evento(s)
            </Badge>
          )}
          {row.mismatchCount > 0 && (
            <Badge variant="destructive" className="text-[10px]">
              {row.mismatchCount} desajuste(s)
            </Badge>
          )}
        </div>
      </div>

      <div className="relative h-10 rounded-md bg-muted/50 border overflow-hidden">
        {/* Hour grid */}
        {HOUR_TICKS.slice(0, -1).map((h) => (
          <div
            key={h}
            className="absolute top-0 bottom-0 border-l border-border/40"
            style={{ left: `${(h / 24) * 100}%` }}
          />
        ))}

        {/* Scheduled windows */}
        {row.schedules.map((s, i) => (
          <div
            key={`sched-${i}`}
            className="absolute top-1 bottom-1 rounded-sm bg-emerald-500/25 border border-emerald-500/50"
            style={{ left: `${s.startPercent}%`, width: `${s.widthPercent}%` }}
            title={`Programado: ${s.startTime} – ${s.endTime}`}
          />
        ))}

        {/* Actual events */}
        {row.events.map((e, i) => (
          <div
            key={`evt-${i}`}
            className="absolute top-0 bottom-0 flex flex-col items-center -translate-x-1/2 z-10"
            style={{ left: `${e.timePercent}%` }}
            title={`${e.label} · ${e.time}${e.manual ? ' (posible manual)' : ''}`}
          >
            <div
              className={cn(
                'w-0.5 flex-1',
                e.isStart && 'bg-blue-500',
                e.isEnd && 'bg-slate-500',
                !e.isStart && !e.isEnd && 'bg-muted-foreground'
              )}
            />
            <div
              className={cn(
                'w-2.5 h-2.5 rounded-full border-2 shrink-0 -mt-0.5',
                e.manual
                  ? 'bg-amber-400 border-amber-600'
                  : e.isStart
                    ? 'bg-blue-500 border-blue-700'
                    : 'bg-slate-400 border-slate-600'
              )}
            />
          </div>
        ))}
      </div>

      {/* Legend row for this relay */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
        {row.schedules.map((s, i) => (
          <span key={i} className="font-mono text-emerald-700">
            ▬ {s.startTime}–{s.endTime}
          </span>
        ))}
        {row.events.map((e, i) => (
          <span
            key={i}
            className={cn('font-mono', e.manual ? 'text-amber-700' : 'text-foreground')}
          >
            {e.isStart ? '▲' : e.isEnd ? '▼' : '●'} {e.time} {e.label}
            {e.manual && ' · manual?'}
          </span>
        ))}
        {row.schedules.length === 0 && row.events.length === 0 && (
          <span>Sin actividad registrada</span>
        )}
      </div>
    </div>
  );
}

export function SwatidScheduleTimeline({ compareData, relays }: SwatidScheduleTimelineProps) {
  const relayLabels = useMemo(() => {
    const map = new Map<number, string>();
    relays.forEach((r) => map.set(r.relayNumber, getSwatidRelayOptionLabel(r)));
    return map;
  }, [relays]);

  const rows = useMemo(
    () => buildTimelineRows(compareData, relayLabels),
    [compareData, relayLabels]
  );

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground text-center">
          No hay programaciones ni eventos de relé para mostrar en el timeline.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Timeline del día — programación vs eventos reales
        </CardTitle>
        <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground pt-1">
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-2 rounded-sm bg-emerald-500/30 border border-emerald-500/50" />
            Ventana programada
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-500 border border-blue-700" />
            Encendido real
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-slate-400 border border-slate-600" />
            Apagado / cancelado
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400 border border-amber-600" />
            Posible acción manual
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Hour axis */}
        <div className="relative h-5 text-[10px] text-muted-foreground font-mono ml-0 mr-0">
          {HOUR_TICKS.map((h) => (
            <span
              key={h}
              className="absolute -translate-x-1/2"
              style={{ left: `${(h / 24) * 100}%` }}
            >
              {h === 24 ? '24:00' : formatHourLabel(h)}
            </span>
          ))}
        </div>

        <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
          {rows.map((row) => (
            <TimelineRowView key={row.relayNumber} row={row} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
